import { Col, Form, FormInstance, Row } from 'antd';
import React, { memo } from 'react';
import { useTheme } from 'styled-components';
import { SimpleSelect as Select, INLTSwitch } from '@xbcb/form-item-components';
import {
  usConsumptionEntrySupplierFields,
  usPostSummaryCorrectionSupplierFields,
} from '@xbcb/party-queries';
import { RecordType, WorkOrderRecordType } from '@xbcb/shared-types';
import { getCodes } from '@xbcb/ui-utils';
import AddressSummary from 'components/AddressSummary';
import RecordSelect from 'components/RecordSelect';
import SPISelect from 'components/SPISelect';
import LineItemCountrySelect from 'components/LineItemCountrySelect';
import { useHtsValues } from 'context/htsValues';
import { useWorkOrderTaskConfiguration, useCurrentUser } from 'libs/hooks';
import { isProject42Enabled } from 'featureFlags';

import mapEmbeddedSupplierFields from 'libs/mapEmbeddedSupplierFields';
import { isReadOnly, shouldRequire } from 'libs/readWriteControl';
import {
  getAddressRequiredFields,
  getMidRequiredField,
  getNameRequiredField,
  getPostalCodeRequiredField,
} from 'libs/requiredEmbeddedFieldHelpers';
import { CommercialInvoiceBaseProps } from 'types';
import { StyledAddressDiv } from './styles';

export interface LineItemManufacturerInfoProps
  extends CommercialInvoiceBaseProps {}

type EmbeddedField = {
  fullNamePath?: (string | number)[];
  validator?: (form: FormInstance<any>) => string[];
};

/**
 * LineItemManufacturerInfo
 *
 * Manufacturer and country selects for a line item. Country and manufacturer
 * are generally only selectable (non-disabled) if the selected product does not
 * set these values.
 */

export const LineItemManufacturerInfo: React.FC<
  LineItemManufacturerInfoProps
> = ({
  fullNamePath,
  localNamePath,
  workOrderType,
  readOnly,
  disabled,
  required,
}) => {
  const theme = useTheme();
  const form = Form.useFormInstance();
  const importDate = Form.useWatch(['arrival', 'importDate'], form);
  const originCountryCode = Form.useWatch(
    [...fullNamePath, 'origin', 'countryCode'],
    form,
  );
  const { readWriteControlsConfig } = useWorkOrderTaskConfiguration();
  const { htsValues } = useHtsValues();
  const relatedManufacturer = [...localNamePath, 'manufacturer', 'related'];
  const isPsc =
    workOrderType === WorkOrderRecordType.US_POST_SUMMARY_CORRECTION;
  const isWorkOrderType = !!workOrderType;
  const currentUser = useCurrentUser();
  const shipperId = currentUser.account?.id;
  const isP42Enabled = isProject42Enabled({ shipperId });

  const entryLineProductId =
    isWorkOrderType &&
    form.getFieldValue([
      // Need to go 2 levels above to get the product reference
      ...fullNamePath.slice(0, -2),
      'product',
      'id',
    ]);

  const manufacturerBaseFullNamePath = [...fullNamePath, 'manufacturer'];
  const manufacturerKey = isWorkOrderType
    ? [...manufacturerBaseFullNamePath, 'supplier']
    : manufacturerBaseFullNamePath;
  const manufacturerRecordSelectField = isWorkOrderType
    ? [...localNamePath, 'manufacturer', 'supplier']
    : [...localNamePath, 'manufacturer'];

  const manufacturerId = Form.useWatch([...manufacturerKey, 'id'], form);

  const codes = getCodes();
  const { CBP } = codes;
  const { CATAIR } = CBP;

  const pscLineReasonCodes = CATAIR.PSC.lineReason;

  const getRequiredEmbeddedFields = () => {
    const requiredEmbeddedFields: EmbeddedField[] = [
      getNameRequiredField(manufacturerBaseFullNamePath),
      ...getAddressRequiredFields(manufacturerBaseFullNamePath),
      getMidRequiredField(manufacturerBaseFullNamePath),
      getPostalCodeRequiredField(manufacturerBaseFullNamePath),
    ];
    return requiredEmbeddedFields;
  };

  let extraManufacturerSelectProps = {};
  if (isWorkOrderType) {
    extraManufacturerSelectProps = {
      versioned: true,
      embeddedFields: isPsc
        ? usPostSummaryCorrectionSupplierFields
        : usConsumptionEntrySupplierFields,
      requiredEmbeddedFields: getRequiredEmbeddedFields(),
      mapEmbeddedFields: mapEmbeddedSupplierFields,
    };
  }

  return (
    <>
      <Row>
        <Col>
          <RecordSelect
            recordType={RecordType.SUPPLIER}
            $itemSize={theme.size.short}
            form={form}
            localNamePath={manufacturerRecordSelectField}
            fullNamePath={manufacturerKey}
            disabled={disabled || entryLineProductId}
            forceAlowRefresh
            label="Manufacturer"
            readOnly={
              readOnly ||
              isReadOnly(
                'invoices.products.lines.manufacturer',
                readWriteControlsConfig,
              )
            }
            required={shouldRequire(
              'invoices.products.lines.manufacturer',
              readWriteControlsConfig,
              required,
            )}
            create={true}
            $removeSpaceBottom
            {...extraManufacturerSelectProps}
          />
          {!!manufacturerId && (
            <StyledAddressDiv>
              <AddressSummary recordId={manufacturerId} />
            </StyledAddressDiv>
          )}
        </Col>
      </Row>
      {/* Show these components only if P42 feature is NOT enabled */}
      {!isP42Enabled && (
        <>
          <Row>
            <Col>
              <INLTSwitch
                onWord="Yes"
                offWord="No"
                label="Is the manufacturer related to the importer?"
                field={relatedManufacturer}
                readOnly={readOnly}
                disabled={disabled}
              />
            </Col>
          </Row>
          <LineItemCountrySelect
            fullNamePath={fullNamePath}
            localNamePath={localNamePath}
            originCountryCode={originCountryCode}
            // Disable the COO selection on Entry line if existing product is selected/used.
            readOnly={readOnly || entryLineProductId}
            disabled={disabled || entryLineProductId}
            required={required}
          />
        </>
      )}
      <Row>
        {originCountryCode && htsValues.lineHtsNumber ? (
          <SPISelect
            form={form}
            disabled={disabled}
            readOnly={readOnly}
            fullNamePath={[...fullNamePath, 'spi']}
            localNamePath={[...localNamePath, 'spi']}
            originCountryCode={originCountryCode}
            htsNumber={htsValues.lineHtsNumber}
            htsObj={htsValues.lineHTSObj}
            entryDate={importDate}
          />
        ) : null}

        {isPsc && (
          <Select
            form={form}
            disabled={disabled}
            readOnly={readOnly}
            localNamePath={[...localNamePath, 'lineReasons']}
            fullNamePath={[...fullNamePath, 'lineReasons']}
            multiple
            map={pscLineReasonCodes}
            label="PSC Reasons"
            max={5}
            required={false}
            $itemSize={theme.size.medium_large}
          />
        )}
      </Row>
    </>
  );
};

export default memo(LineItemManufacturerInfo);
